import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import {
  GET_NUMBERS,
  GET_PRICING_NUMBERS,
  GET_SINGLE_PRICING,
  GetNumbersAction,
  GetNumbersFailureAction,
  GetNumbersSuccessAction,
  GetPricingNumbersAction,
  GetPricingNumbersFailureAction,
  GetPricingNumbersSuccessAction,
  GetSinglePricingAction,
  GetSinglePricingFailureAction,
  GetSinglePricingSuccessAction,
  ORDER_INITIAL_NUMBER,
  ORDER_INITIAL_NUMBER_SUCCESS,
  ORDER_NUMBER,
  ORDER_NUMBER_PAYMENT,
  OrderInitialNumberFailureAction,
  OrderInitialNumberSuccessAction,
  OrderNumberAction,
  OrderNumberFailureAction,
  OrderNumberPaymentAction,
  PricingNumbersAction,
  REGION_FOR_NUMBER_CHOSEN,
  RegionForNumberChosenAction
} from './pricing-numbers.action';
import { PricingNumbersService } from '../../shared/services/pricing-numbers.service';
import {
  ActionType,
  GetNumbersFromRegionState,
  MsisdnRequestDto,
  OrderNumberRequest,
  PhoneNumber,
  PricingItem,
  PricingNumbers
} from './pricing-numbers';
import { Router } from '@angular/router';
import { MsisdnService } from '../../shared/msisdn/msisdn.service';
import { AccountService } from '../../shared/services/account.service';
import { Ng1ModalService } from '../../shared/ng1/ng1-modal.service';
import { PaymentParams } from '../../account/account.model';
import { PaymentService } from '../../shared/services/payment.service';
import {GoToNextStateAction} from "../../first-steps/first-steps.actions";

@Injectable()
export class PricingNumbersEffects {
  @Effect()
  getPricingNumbers$: Observable<PricingNumbersAction> = this.actions$.pipe(
    ofType(GET_PRICING_NUMBERS),
    map((action: GetPricingNumbersAction) => action.payload),
    switchMap(payload =>
      this.pricingNumbersService
        .getPricingNumbersWithDocuments(payload)
        .pipe(
          map((response: PricingNumbers) => new GetPricingNumbersSuccessAction(response)),
          catchError(error => of(new GetPricingNumbersFailureAction(error)))
        )
    )
  );

  @Effect()
  getNumbers$: Observable<PricingNumbersAction> = this.actions$.pipe(
    ofType(GET_NUMBERS),
    map((action: GetNumbersAction) => action.payload),
    switchMap(payload => {
      return this.pricingNumbersService
        .getPhoneNumbers(payload.pricingId, payload.searchText)
        .pipe(
          map((response: PhoneNumber[]) => new GetNumbersSuccessAction(response)),
          catchError(error => of(new GetNumbersFailureAction(error)))
        );
    })
  );

  @Effect()
  getSinglePricing$ = this.actions$.pipe(
    ofType(GET_SINGLE_PRICING),
    map((action: GetSinglePricingAction) => action.payload),
    switchMap(payload => {
      return this.pricingNumbersService
        .getSinglePricing(payload.pricingId)
        .pipe(
          map((response: PricingItem) => new GetSinglePricingSuccessAction(response)),
          catchError(error => of(new GetSinglePricingFailureAction(error)))
        );
    })
  );

  @Effect()
  orderNumber$ = this.actions$.pipe(
    ofType(ORDER_NUMBER),
    map((action: OrderNumberAction) => action.payload),
    map((payload: OrderNumberRequest) => this.mapToAssignRequest(payload, 'ORDER')),
    switchMap((payload: MsisdnRequestDto) =>
      this.msisdnService.limitsExceeded().pipe(
        map(limitsExceeded => ({
          limitsExceeded,
          payload
        }))
      )
    ),
    switchMap(({ limitsExceeded, payload }) => {
      if (limitsExceeded) {
        this.ng1ModalService.lazyModalService.showUpgradeAccountModal();
        return;
      }
      return this.msisdnService
        .assign(payload)
        .pipe(
          map((payment: PaymentParams) => new OrderNumberPaymentAction(payment, payload)),
          catchError(error => of(new OrderNumberFailureAction(error)))
        );
    })
  );

  @Effect()
  orderInitialNumber$ = this.actions$.pipe(
    ofType(ORDER_INITIAL_NUMBER),
    map((action: OrderNumberAction) => action.payload),
    map((payload: OrderNumberRequest) => this.mapToAssignRequest(payload, 'GET_INITIAL')),
    switchMap((payload: MsisdnRequestDto) => {
      return this.msisdnService
        .assignInitial(payload)
        .pipe(
          switchMap(() => [
            new OrderInitialNumberSuccessAction(),
            new GoToNextStateAction()
          ]),
          catchError(error => of(new OrderInitialNumberFailureAction(error)))
        );
    })
  );

  @Effect({ dispatch: false })
  afterInitialNumber$ = this.actions$.pipe(
    ofType(ORDER_INITIAL_NUMBER_SUCCESS),
    tap(() => {
      this.accountService.changeAccountConfig({ data: true });
    })
  );

  @Effect({ dispatch: false })
  afterOrderNumberPayment$ = this.actions$.pipe(
    ofType(ORDER_NUMBER_PAYMENT),
    tap((action: OrderNumberPaymentAction) => {
      const selectedMsisdnDisplayedValue = action.payload.selectedMsisdnDisplayedValue;
      this.paymentService.handleSubscriptionPayment(
        action.payment,
        `phone-numbers-${selectedMsisdnDisplayedValue || ''}`
      );
    })
  );

  @Effect({ dispatch: false })
  redirectAfterRegionForNumberChosen$ = this.actions$.pipe(
    ofType(REGION_FOR_NUMBER_CHOSEN),
    map((action: RegionForNumberChosenAction) => action.payload),
    switchMap(payload => {
      return this.pricingNumbersService.availableNumbersState(payload.pricingId).pipe(
        tap((response: GetNumbersFromRegionState) => {
          const baseUrl = payload.fullyConfigured ? '/get-new-number' : '/get-first-new-number';
          if (response === GetNumbersFromRegionState.CAN_GET_RANDOM_NUMBER) {
            this.router.navigate([baseUrl, 'pricing', payload.pricingId, 'final-step']);
          } else {
            this.router.navigate([baseUrl, 'numbers', payload.pricingId]);
          }
        })
      );
    })
  );

  private mapToAssignRequest(payload: OrderNumberRequest, actionType: ActionType): MsisdnRequestDto {
    const request: MsisdnRequestDto = {
      label: payload.label,
      pricingId: payload.pricingId,
      actionType: actionType
    };
    if (payload.selectedMsisdn) {
      request.selectedMsisdnDisplayedValue = payload.selectedMsisdn.number;
      request.selectedMsisdnId = payload.selectedMsisdn.id;
    }
    return request;
  }

  constructor(
    private actions$: Actions,
    private pricingNumbersService: PricingNumbersService,
    private router: Router,
    private msisdnService: MsisdnService,
    private accountService: AccountService,
    private ng1ModalService: Ng1ModalService,
    private paymentService: PaymentService
  ) {}
}
